import { ReactNode, createContext, useContext } from 'react';

import { Box, Button, Result, Spinner } from '@aicon-dev/caas-ui';
import { UseQueryResult, useQueries } from '@tanstack/react-query';
import { useMatch, useResolvedPath } from 'react-router-dom';

import { Header } from '~/components/ui/Header';
import { useAuthContext } from '~/modules/auth/context/AuthProvider';
import { Link, useParams } from '~/router';

import { getMobileProjectDashboard } from '../components/ProjectDashboard/libs/apis';
import { findOneProject, getProjectClientSettings } from '../libs/apis';
import { FIND_ONE_PROJECT_QUERY_KEY } from '../libs/queries/useFindOneProject';
import { GET_PROJECT_CLIENT_SETTINGS_QUERY_KEY } from '../libs/queries/useProjectClientSettings';
import { ProjectClientSettingsDto, ProjectModel } from '../libs/types';

interface ProjectContextValues {
  project?: ProjectModel;
  settings?: ProjectClientSettingsDto;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dashboardQuery?: UseQueryResult<any, Error>;
}

export const ProjectContext = createContext<ProjectContextValues>({});

interface ProjectProviderProps {
  children: ReactNode;
}

export const ProjectProvider = (props: ProjectProviderProps) => {
  const { children } = props;
  const { projectId } = useParams('/projects/:projectId');
  const { userInfo } = useAuthContext();
  const dashboardResolved = useResolvedPath('/projects/:projectId');
  const dashboardMatch = useMatch({ path: dashboardResolved.pathname, end: true });

  const [
    { data: project, isLoading, isError },
    { data: settings, isLoading: isSettingsLoading },
    dashboardQuery,
  ] = useQueries({
    queries: [
      {
        queryKey: [FIND_ONE_PROJECT_QUERY_KEY, projectId, userInfo?._id],
        queryFn: async () => {
          const { data } = await findOneProject(projectId);
          return data;
        },
        gcTime: Infinity,
        /**
         * 1분 이내에 prefetch된 데이터가 있을 경우 fetch하지 않음
         */
        staleTime: 1000 * 60, // 1분
      },
      {
        queryKey: [GET_PROJECT_CLIENT_SETTINGS_QUERY_KEY, projectId],
        queryFn: async () => {
          return (await getProjectClientSettings(projectId)).data;
        },
        gcTime: Infinity,
        /**
         * 1분 이내에 prefetch된 데이터가 있을 경우 fetch하지 않음
         */
        staleTime: 1000 * 60, // 1분
      },
      {
        queryKey: ['mobile-project-dashboard', projectId],
        queryFn: async () => {
          const { data } = await getMobileProjectDashboard(projectId, { units: 'metric' });
          return data;
        },
        gcTime: Infinity,
        retry: false,
        enabled: !!dashboardMatch, // 프로젝트 대시보드일 경우
        /**
         * 1분 이내에 prefetch된 데이터가 있을 경우 fetch하지 않음
         */
        staleTime: 1000 * 60, // 1분
      },
    ],
  });

  if (isLoading || dashboardQuery.isLoading || isSettingsLoading) {
    return (
      <div>
        <Header />
        <Box p="16">
          <Result
            size="medium"
            status="loading"
            icon={<Spinner color="primary.500" />}
            title="프로젝트에 접속하고 있습니다."
          />
        </Box>
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <Header />
        <Box p="16">
          <Result size="medium" status="error" title="프로젝트에 접근할 수 없습니다.">
            <Button asChild color="primary" variant="filled">
              <Link to="/">홈으로</Link>
            </Button>
          </Result>
        </Box>
      </div>
    );
  }

  return (
    <ProjectContext.Provider value={{ project, settings, dashboardQuery }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjectContext = () => useContext(ProjectContext);
